// The Forms half of the API client: templates and their frozen versions.
//
// The content types mirror `server/src/lib/formTemplate.ts` exactly. They are
// duplicated rather than shared because the two halves of this app do not
// share a module — and the server normalizes everything it is sent anyway, so
// the worst a drifted type here can do is stop the builder offering a setting,
// never store a bad document.
import { ApiError, request } from './api';

export const FIELD_TYPES = [
  'text',
  'number',
  'date',
  'choice',
  'checklist',
  'photo',
  'signature',
  'meter_reading',
  // The ninth (round 28): a dropdown of the company's own things.
  'entity_ref',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export const ENTITY_KINDS = ['machine', 'job', 'person', 'client'] as const;
export type EntityKind = (typeof ENTITY_KINDS)[number];

export type LinkKind = 'job' | 'asset' | 'any';
export type TemplateStatus = 'draft' | 'published' | 'archived';

export interface FormField {
  key: string;
  type: FieldType;
  label: string;
  required: boolean;
  multiline?: boolean;
  unit?: string;
  options?: string[];
  max_photos?: number;
  /** entity_ref only: which kind of thing the dropdown offers. */
  entity?: EntityKind;
  /** entity_ref with entity 'machine' only: the question's own filter. */
  asset_filter?: AssetFilter;
}

export interface FormSection {
  key: string;
  title: string;
  fields: FormField[];
}

/** Which machines an asset form is for (round 28). Absent = every machine. */
export interface AssetFilter {
  kinds: string[];
  machine_type_ids: string[];
}

export interface FormContent {
  format: 1;
  title: string;
  link_kind: LinkKind;
  asset_filter?: AssetFilter;
  sections: FormSection[];
}

export interface FormTemplate {
  id: string;
  name: string;
  description: string | null;
  status: TemplateStatus;
  linkKind: LinkKind;
  content: FormContent;
  currentVersion: {
    id: string;
    version: number;
    publishedAt: string;
    /** Who pressed Publish — the templates list's Published column. */
    publishedByName: string | null;
  } | null;
  /** The draft has moved on from what is live. */
  hasUnpublishedChanges: boolean;
  isSampleData: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FormVersion {
  id: string;
  templateId: string;
  version: number;
  content: FormContent;
  isCurrent: boolean;
  publishedById: string | null;
  publishedAt: string;
}

/** A history entry — the version's facts without its frozen document. */
export type FormVersionSummary = Omit<FormVersion, 'content'> & {
  /** Who pressed Publish, for the builder's Versions tab. */
  publishedByName: string | null;
};

/**
 * A stable key for a new section or field.
 *
 * Generated on the CLIENT and never regenerated, because the key is the join
 * between a question and every answer already stored against it. Renaming one
 * would orphan them — which is why the server checks the shape of a key and
 * never invents one.
 */
export function newKey(prefix: 'f' | 's'): string {
  const random = Math.random().toString(36).slice(2, 8).padEnd(6, '0');
  return `${prefix}_${random}`;
}

/** Human labels for the eight field types, in the order the builder offers them. */
export const FIELD_TYPE_LABEL: Record<FieldType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  choice: 'Choose one',
  checklist: 'Checklist',
  photo: 'Photos',
  signature: 'Signature',
  meter_reading: 'Meter reading',
  entity_ref: 'Pick from the app',
};

/** What each type is for, shown under the picker so a manager can choose. */
export const FIELD_TYPE_HINT: Record<FieldType, string> = {
  text: 'A written answer.',
  number: 'A number, with an optional unit.',
  date: 'A single date.',
  choice: 'One option from a list.',
  checklist: 'Any number of options from a list.',
  photo: 'Photos taken on the phone.',
  signature: 'Signed with a finger.',
  meter_reading: 'The machine’s hours or kilometres. Asset forms only, and only one.',
  entity_ref: 'A dropdown of your machines, jobs, people or clients.',
};

export const forms = {
  /** `all` is the All tab: one response, archived included. */
  listTemplates: (status?: TemplateStatus | 'all') =>
    request<{ templates: FormTemplate[] }>(
      `/forms/templates${status ? `?status=${status}` : ''}`
    ),
  getTemplate: (id: string) =>
    request<{ template: FormTemplate; versions: FormVersionSummary[] }>(`/forms/templates/${id}`),
  createTemplate: (body: { name: string; description?: string | null; linkKind: LinkKind }) =>
    request<{ template: FormTemplate }>('/forms/templates', { method: 'POST', body }),
  updateTemplate: (id: string, body: Record<string, unknown>) =>
    request<{ template: FormTemplate }>(`/forms/templates/${id}`, { method: 'PATCH', body }),
  publishTemplate: (id: string) =>
    request<{ template: FormTemplate; version: FormVersion }>(
      `/forms/templates/${id}/publish`,
      { method: 'POST', body: {} }
    ),
  archiveTemplate: (id: string, archived: boolean) =>
    request<{ template: FormTemplate }>(`/forms/templates/${id}/archive`, {
      method: 'POST',
      body: { archived },
    }),
  /** Copies the WORKING DRAFT into a fresh draft with no version history. */
  duplicateTemplate: (id: string) =>
    request<{ template: FormTemplate }>(`/forms/templates/${id}/duplicate`, {
      method: 'POST',
      body: {},
    }),
  /** One frozen version, document included — the Versions tab's viewer. */
  getTemplateVersion: (id: string, versionId: string) =>
    request<{ version: FormVersion }>(`/forms/templates/${id}/versions/${versionId}`),
};

/** Pulls the publish problems out of a refused publish. */
export function publishProblemsFrom(error: unknown): string[] {
  const err = error as { code?: string; details?: { problems?: string[] } } | null;
  if (!err || err.code !== 'unprocessable') return [];
  return err.details?.problems ?? [];
}

// --- Filling one in (migration 015) -----------------------------------

export type SubmissionStatus = 'draft' | 'submitted';

export interface SubmissionFile {
  id: string;
  fieldKey: string;
  kind: 'photo' | 'signature';
}

/** The machine as it read the day the form was started (round 28). */
export interface ResourceSnapshot {
  name: string;
  identifier: string | null;
  registrationNumber: string | null;
  kind: string;
  category: string | null;
  machineType: string | null;
}

export interface Submission {
  id: string;
  templateId: string;
  templateVersionId: string;
  kind: 'job' | 'asset';
  jobId: string | null;
  /** Survives the job being deleted — that is what it is for. */
  jobNumber: string | null;
  resourceId: string | null;
  /** Null on job-linked and pre-round-28 rows; lists stay on the live join. */
  resourceSnapshot: ResourceSnapshot | null;
  status: SubmissionStatus;
  answers: Record<string, unknown>;
  files: SubmissionFile[];
  submittedByName: string | null;
  submittedAt: string | null;
  /**
   * The phone's claim of when it was completed there (round 42): set when a
   * form filled in with no signal reached Dayboard later. `submittedAt` stays
   * what the server witnessed; screens state both when they differ.
   */
  deviceSubmittedAt?: string | null;
  pdfReady: boolean;
  createdAt: string;
  updatedAt: string;
}

/** A row in the submissions list. Deliberately lighter than a Submission. */
export interface SubmissionListItem {
  id: string;
  templateId: string;
  templateName: string;
  kind: 'job' | 'asset';
  jobId: string | null;
  jobNumber: string | null;
  resourceId: string | null;
  status: SubmissionStatus;
  submittedByName: string | null;
  /** Who STARTED it, resolved live: an unfinished form needs a name too. */
  startedByName: string | null;
  submittedAt: string | null;
  createdAt: string;
}

export interface Fillable {
  templates: {
    id: string;
    name: string;
    description: string | null;
    linkKind: LinkKind;
    versionId: string;
    version: number;
  }[];
  /** A field worker's only window into plant and vehicles: id and label only. */
  assets: { id: string; label: string }[];
  jobs: { id: string; number: string; title: string }[];
}

/**
 * A meter on the machine a form is about.
 *
 * Comes back with the SUBMISSION rather than from the fleet API, because that
 * one is manager-only — and a prestart with a meter question that a field
 * worker cannot see is a prestart they cannot fill in.
 */
export interface SubmissionMeter {
  id: string;
  label: string;
  unit: string;
  currentReading: number | null;
}

/** One machine, as the Step-1 picker offers it — the MachineCard's fields. */
export interface TemplateAsset {
  id: string;
  label: string;
  name: string;
  identifier: string | null;
  registrationNumber: string | null;
  kind: string;
  category: string | null;
  machineTypeName: string | null;
}

/**
 * One form as the offline pack carries it (round 42): enough to start and
 * fill it in with no signal. `content` is the CURRENT version's questions and
 * `versionId` is what a phone start pins.
 */
export interface PackForm {
  id: string;
  name: string;
  description: string | null;
  linkKind: LinkKind;
  versionId: string;
  version: number;
  content: FormContent;
  /** The machines this form offers, by id into `OfflinePack.machines`. */
  machineIds: string[];
  /** The dropdown behind each "Pick from the app" question, by field key. */
  options: Record<string, { id: string; label: string }[]>;
}

/**
 * Everything a phone needs to fill in any published form with no signal
 * (round 42). Every list is the one the live routes already hand this person.
 */
export interface OfflinePack {
  generatedAt: string;
  forms: PackForm[];
  machines: TemplateAsset[];
  /** By machine id. */
  meters: Record<string, SubmissionMeter[]>;
  jobs: { id: string; number: string; title: string }[];
}

export const formFill = {
  fillable: () => request<Fillable>('/forms/fillable'),
  offlinePack: () => request<OfflinePack>('/forms/offline-pack'),
  /**
   * The options behind one "Pick from the app" question, resolved from the
   * draft's PINNED content — correct across a mid-draft republish.
   */
  refOptions: (submissionId: string, fieldKey: string) =>
    request<{ options: { id: string; label: string }[] }>(
      `/forms/submissions/${submissionId}/options?field=${encodeURIComponent(fieldKey)}`
    ),
  /**
   * The machines ONE form is offered against, already filtered server-side
   * by the same predicate the start route enforces. `filtered` lets the
   * empty state say "no machines match this form" rather than "no machines".
   */
  templateAssets: (templateId: string) =>
    request<{ assets: TemplateAsset[]; filtered: boolean }>(
      `/forms/templates/${templateId}/assets`
    ),
  /**
   * `id` is named by the phone, so a resend after a lost reply finds the row
   * it already made. `versionId` is sent ONLY for a form started with no
   * signal: the version the phone showed the person (round 42).
   */
  start: (body: {
    templateId: string;
    jobId?: string | null;
    resourceId?: string | null;
    id?: string;
    versionId?: string;
  }) =>
    request<{ submission: Submission; content: FormContent; meters: SubmissionMeter[] }>(
      '/forms/submissions',
      { method: 'POST', body }
    ),
  get: (id: string) =>
    request<{
      submission: Submission;
      content: FormContent;
      meters: SubmissionMeter[];
      staleVersion: boolean;
      /** The job's title, resolved live (the NUMBER is the snapshot). */
      jobTitle: string | null;
    }>(`/forms/submissions/${id}`),
  /**
   * Removes one photo (or the signature) from a DRAFT. The server refuses on
   * a submitted form: a record keeps every byte.
   */
  deleteFile: (submissionId: string, fileId: string) =>
    request<void>(`/forms/submissions/${submissionId}/files/${fileId}`, { method: 'DELETE' }),
  save: (id: string, answers: Record<string, unknown>) =>
    request<{ submission: Submission }>(`/forms/submissions/${id}`, {
      method: 'PATCH',
      body: { answers },
    }),
  /** `deviceSubmittedAt`: when Submit was pressed on the phone, if earlier. */
  submit: (id: string, deviceSubmittedAt?: string) =>
    request<{ submission: Submission; pdfReady: boolean }>(
      `/forms/submissions/${id}/submit`,
      { method: 'POST', body: deviceSubmittedAt ? { deviceSubmittedAt } : {} }
    ),
  discard: (id: string) => request<void>(`/forms/submissions/${id}`, { method: 'DELETE' }),
  /** One row per form: the Submissions page opens on this rollup. */
  overview: () =>
    request<{
      forms: {
        templateId: string;
        templateName: string;
        submittedCount: number;
        draftCount: number;
        lastSubmittedAt: string | null;
      }[];
    }>('/forms/submissions/overview'),
  list: (
    params: {
      jobId?: string;
      resourceId?: string;
      templateId?: string;
      status?: string;
      /** YYYY-MM-DD, against the SUBMITTED date — drafts never match a range. */
      from?: string;
      to?: string;
      /** Mentioned, not filed against (round 28). Submitted forms only. */
      mentionsResourceId?: string;
      mentionsJobId?: string;
      page?: number;
      perPage?: number;
    } = {}
  ) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) search.set(key, String(value));
    const query = search.toString();
    return request<{
      submissions: SubmissionListItem[];
      total: number;
      page: number;
      perPage: number;
    }>(`/forms/submissions${query ? `?${query}` : ''}`);
  },
  fileUrl: (fileId: string) => `/api/forms/files/${fileId}`,
  pdfUrl: (submissionId: string) => `/api/forms/submissions/${submissionId}/pdf`,
  /** The same PDF, saved under a server-built filename a person can file. */
  pdfDownloadUrl: (submissionId: string) =>
    `/api/forms/submissions/${submissionId}/pdf?download=1`,
};

/**
 * Uploads one photo or signature.
 *
 * Not through `request`, because this is multipart rather than JSON — the same
 * split the documents uploader makes.
 */
export async function uploadSubmissionFile(
  submissionId: string,
  fieldKey: string,
  kind: 'photo' | 'signature',
  file: Blob,
  filename: string,
  /** Named by the phone (round 42), so a resend cannot add it twice. */
  fileId?: string
): Promise<SubmissionFile> {
  const body = new FormData();
  body.append('fieldKey', fieldKey);
  body.append('kind', kind);
  if (fileId) body.append('id', fileId);
  body.append('file', file, filename);

  let response: Response;
  try {
    response = await fetch(`/api/forms/submissions/${submissionId}/files`, {
      method: 'POST',
      credentials: 'same-origin',
      body,
    });
  } catch {
    // The same shape `request` throws, so the offline queue can tell "no
    // signal" from "the server said no" without a second vocabulary.
    throw new ApiError(0, 'network', 'Could not reach Dayboard. Check your connection.');
  }
  const payload = (await response.json().catch(() => null)) as
    | { file?: SubmissionFile; error?: { code?: string; message?: string } }
    | null;
  if (!response.ok || !payload?.file) {
    throw new ApiError(
      response.status,
      payload?.error?.code ?? 'upload_failed',
      payload?.error?.message ?? 'That file did not upload.'
    );
  }
  return payload.file;
}

/** Pulls the named problems out of a refused submit. */
export function submitProblemsFrom(error: unknown): string[] {
  const err = error as { code?: string; details?: { problems?: string[] } } | null;
  if (!err || err.code !== 'unprocessable') return [];
  return err.details?.problems ?? [];
}
